import { useCallback, useEffect, useRef } from 'react';
import { EndpointLock, useLazyGetCaseDocumentsQuery, useLazyGetCaseQuery } from '@/api';
import { useLazyPostDocumentBulkCreateQuery } from '@/api/document/documentBulkCreate';
import { useLazyPostDocumentBulkGetUploadQuery } from '@/api/document/documentBulkGetUpload';
import { useLazyPostDocumentBulkUploadCompleteQuery } from '@/api/document/documentBulkUploadComplete';
import { useAzureFileUpload } from '@repo/file-uploader';
import {
  selectCaseDocumentsPagination,
  selectCaseIdData,
  selectIsEndpointLockActive,
  setUploadingDocumentIds,
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from '@/store';

const BULK_UPLOAD_COMPLETE_INTERVAL_MS = 5000;

export const useDragAndDropFilesUpload = () => {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const caseId = useAppSelector(selectCaseIdData);
  const caseDocumentsPagination = useAppSelector(selectCaseDocumentsPagination);
  const isCaseCloning = useAppSelector((state) => selectIsEndpointLockActive(state, EndpointLock.CASE_CLONING));

  const [triggerPostDocumentBulkCreate] = useLazyPostDocumentBulkCreateQuery();
  const [triggerPostDocumentBulkGetUpload] = useLazyPostDocumentBulkGetUploadQuery();
  const [triggerPostDocumentBulkUploadComplete] = useLazyPostDocumentBulkUploadCompleteQuery();
  const [triggerGetCase] = useLazyGetCaseQuery();
  const [triggerGetCaseDocuments] = useLazyGetCaseDocumentsQuery();

  const completedDocIdsRef = useRef<string[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const caseIdRef = useRef(caseId);
  caseIdRef.current = caseId;
  // Set by the effect below: true only while this page is mounted and the case is being cloned
  const isHoldingCompletedUploadsRef = useRef(false);

  // Synchronously-mutated set that tracks active uploads, avoids stale closure
  // issues when multiple onUploadComplete callbacks fire before React re-renders.
  const activeUploadIdsRef = useRef<Set<string>>(new Set());

  const flushCompletedUploads = useCallback(() => {
    // Only matters if the backend lets a clone start while uploads are still in flight: it then refuses their
    // confirmations until the clone has finished, so they're held rather than retried on the 5 s timer, where each
    // refusal would re-show the lock's toast
    if (completedDocIdsRef.current.length === 0 || isHoldingCompletedUploadsRef.current) return;

    const idsToFlush = [...completedDocIdsRef.current];
    completedDocIdsRef.current = [];

    triggerPostDocumentBulkUploadComplete({ documentIds: idsToFlush })
      .unwrap()
      .then(() => {
        if (caseIdRef.current) {
          triggerGetCase({ caseId: caseIdRef.current });
        }
      })
      .catch((error) => {
        console.error('Failed to complete bulk upload:', error);
        completedDocIdsRef.current.push(...idsToFlush);
      });
  }, [triggerPostDocumentBulkUploadComplete, triggerGetCase]);

  const stopBatchTimer = useCallback(() => {
    if (flushTimerRef.current) {
      clearInterval(flushTimerRef.current);
      flushTimerRef.current = null;
    }
  }, []);

  const startBatchTimer = useCallback(() => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setInterval(() => {
      flushCompletedUploads();
    }, BULK_UPLOAD_COMPLETE_INTERVAL_MS);
  }, [flushCompletedUploads]);

  useEffect(() => {
    return () => stopBatchTimer();
  }, [stopBatchTimer]);

  // Holds completed uploads while the case is being cloned and sends them once it has finished. Uploads can
  // also finish after this page has gone; those are sent straight away, as before, for the backend to decide.
  useEffect(() => {
    isHoldingCompletedUploadsRef.current = isCaseCloning;
    if (!isCaseCloning) flushCompletedUploads();

    return () => {
      isHoldingCompletedUploadsRef.current = false;
    };
  }, [isCaseCloning, flushCompletedUploads]);

  // Leaving the page hands anything still held to the backend, as nothing here will send it later
  useEffect(() => {
    return () => {
      isHoldingCompletedUploadsRef.current = false;
      flushCompletedUploads();
    };
  }, [flushCompletedUploads]);

  const { uploadFile, deleteUpload } = useAzureFileUpload({
    onUploadComplete: (documentId: string) => {
      completedDocIdsRef.current.push(documentId);
      deleteUpload(documentId);

      activeUploadIdsRef.current.delete(documentId);
      dispatch(setUploadingDocumentIds([...activeUploadIdsRef.current]));

      if (activeUploadIdsRef.current.size === 0) {
        stopBatchTimer();
        flushCompletedUploads();
      }
    },
  });

  const onFilesStartUpload = async (selectedFiles: File[]) => {
    if (!caseId) return [];

    let hasCreatedDocuments = false;

    try {
      const bulkCreateResponse = await triggerPostDocumentBulkCreate({
        caseId,
        documents: selectedFiles.map((file) => ({
          documentName: file.name,
          documentSizeMb: +(file.size / (1024 * 1024)),
        })),
      }).unwrap();
      hasCreatedDocuments = true;

      const remainingFiles = [...selectedFiles];
      const fileDocumentPairs = bulkCreateResponse.documents.map((doc) => {
        const fileIndex = remainingFiles.findIndex((f) => f.name === doc.documentName);
        const file = fileIndex >= 0 ? remainingFiles.splice(fileIndex, 1)[0] : null;
        return {
          file,
          documentId: doc.documentId,
        };
      });

      const validDocumentIds = fileDocumentPairs.filter((pair) => pair.file !== null).map((pair) => pair.documentId);

      const bulkGetUploadResponse = await triggerPostDocumentBulkGetUpload({
        documentIds: validDocumentIds,
      }).unwrap();

      const uploadsByDocId = new Map(bulkGetUploadResponse.uploads.map((u) => [u.documentId, u]));

      const uploadFileInfos = fileDocumentPairs.map(({ documentId, file }) => {
        const upload = uploadsByDocId.get(documentId);
        return {
          file,
          documentId,
          uploadPath: upload?.uploadPath ?? null,
          uploadToken: upload?.uploadToken ?? null,
        };
      });

      const uploadedFileDocumentIds = uploadFileInfos.map(({ file, documentId, uploadPath, uploadToken }) => {
        if (documentId && file && uploadPath && uploadToken) {
          uploadFile(documentId, file, uploadPath, uploadToken);
          return documentId;
        }
        return null;
      });

      const validIds = uploadedFileDocumentIds.filter((id): id is string => typeof id === 'string');

      validIds.forEach((id) => activeUploadIdsRef.current.add(id));

      if (validIds.length > 0) {
        startBatchTimer();
      }

      dispatch(setUploadingDocumentIds([...activeUploadIdsRef.current]));
    } catch (error) {
      console.error('Failed to bulk create documents:', error);
      // A failure after bulk-create (a lock refusal included) leaves its documents on the case, so show their rows -
      // unless the page has moved to another case meanwhile, as the response would replace that case's list
      if (hasCreatedDocuments && selectCaseIdData(store.getState()) === caseId) {
        triggerGetCaseDocuments({ caseId, ...caseDocumentsPagination });
      }
    }
  };

  return {
    onFilesStartUpload,
  };
};
